import React from "react";
import { ESTADOS_INTERES } from "../constants/interesados";
import { usePermissions } from "../../../../utils/permissions";

const normalizeKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

function Modal({ open, title, onClose, size = "modal-lg", children }) {
  if (!open) return null;
  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
      aria-modal="true"
      role="dialog"
    >
      <div className={`modal-dialog ${size} modal-dialog-centered`} role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

const normalizeFuente = (form, fuentesOptions) => {
  const option = fuentesOptions.find((f) => String(f.value) === String(form.fuente_origen_id));
  const nombre = option?.label || form.fuente_origen_nombre || "";
  return nombre
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
};

const resolveTipoReferido = (clave) => {
  if (!clave) return "";
  if (clave === "docente") return "docente";
  if (["estudiante", "administrativo", "externo"].includes(clave)) return clave;
  return "";
};

const buildFlags = (form, fuentesOptions) => {
  const clave = normalizeFuente(form, fuentesOptions);
  const tipoReferido = resolveTipoReferido(clave);
  return {
    fuenteHabilitaReferido: ["administrativo", "estudiante", "docente"].includes(clave),
    fuenteHabilitaEmpresa: clave === "empresa",
    tipoReferido,
  };
};

export default function InteresadoFormModal({
  modal,
  formNuevo,
  setFormNuevo,
  formEditar,
  setFormEditar,
  saving,
  deleting,
  loadingDetalle,
  buscandoDoc,
  cerrarModal,
  handleCrear,
  handleActualizar,
  handleEliminar,
  buscarDocumento,
  interesadoEditando,
  fuentesOptions = [],
  empresasOptions = [],
  referidosOpciones = [],
  referidoSearchTerm = "",
  setReferidoSearchTerm = () => {},
  cargandoReferidos = false,
}) {
  const { hasPermission } = usePermissions();
  const puedeEditarFecha = hasPermission("migracion.fecha_retroactiva");

  const [referidoMenuAbierto, setReferidoMenuAbierto] = React.useState(false);
  const [empresaMenuAbierto, setEmpresaMenuAbierto] = React.useState(false);
  const [empresaSearchTerm, setEmpresaSearchTerm] = React.useState("");
  React.useEffect(() => {
    if (!modal) {
      setReferidoMenuAbierto(false);
      setEmpresaMenuAbierto(false);
      setEmpresaSearchTerm("");
    }
  }, [modal]);

  const ensureSelectedReferido = (options, form, interesadoEditando) => {
    if (!form.referido_por_id) return options;
    const exists = options.some((ref) => String(ref.id) === String(form.referido_por_id));
    if (exists) return options;
    const nombreBase = (interesadoEditando?.referidoNombre || "").trim();
    const docBase = (interesadoEditando?.referidoDocumento || "").trim();
    return [
      ...options,
      {
        id: form.referido_por_id,
        nombres: nombreBase || docBase || "",
        apellidos: "",
        documento_identidad: docBase || "",
        estado_academico: interesadoEditando?.referidoEstado || "",
        persist: true,
      },
    ];
  };

  const buildReferidoLabel = (ref, seleccionadoId) => {
    const nombre = `${ref.nombres || ""} ${ref.apellidos || ""}`.trim();
    const documento = ref.documento_identidad || ref.documento || "";
    const estado = ref.estado_academico ? ` (${ref.estado_academico})` : "";
    if (nombre || documento) return `${nombre || documento}${estado}`;
    if (seleccionadoId && String(ref.id) === String(seleccionadoId)) {
      const fallbackNombre =
        (interesadoEditando?.referidoNombre || "").trim() ||
        (interesadoEditando?.referidoDocumento || "").trim();
      if (fallbackNombre) return `${fallbackNombre}${estado}`;
    }
    return `ID ${ref.id}${estado}`;
  };

  const renderForm = (form, setForm, onSubmit, isEdit = false) => {
    const { fuenteHabilitaReferido, fuenteHabilitaEmpresa, tipoReferido } = buildFlags(form, fuentesOptions);
    const showReferido = fuenteHabilitaReferido;
    const opcionesReferidos = ensureSelectedReferido(referidosOpciones, form, interesadoEditando);
    const opcionesReferidosFiltradas = opcionesReferidos.filter((ref) => {
      if (!tipoReferido) return true;
      if (form.referido_por_id && String(ref.id) === String(form.referido_por_id)) return true;
      const estado = normalizeKey(ref.estado_academico || ref.tipo || "");
      if (!estado) return true;
      if (tipoReferido === "docente") return estado.includes("docente");
      return estado.includes(tipoReferido);
    });
    const referidoSeleccionado = opcionesReferidosFiltradas.find(
      (ref) => String(ref.id) === String(form.referido_por_id || "")
    );
    const referidoSeleccionadoLabel = referidoSeleccionado
      ? buildReferidoLabel(referidoSeleccionado, form.referido_por_id)
      : "";
    const referidoInputValue = referidoSearchTerm || referidoSeleccionadoLabel;
    const empresaSeleccionadaLabel =
      empresasOptions.find((opt) => String(opt.value) === String(form.empresa_origen_id))?.label ||
      form.empresa_nombre ||
      "";
    const empresaInputValue = empresaSearchTerm || empresaSeleccionadaLabel;
    const empresaOpcionesFiltradas = (empresasOptions || []).filter((opt) =>
      (opt.label || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase()
        .includes(
          (empresaSearchTerm || "")
            .toString()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .trim()
            .toLowerCase()
        )
    );

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.();
        }}
      >
        <div className="modal-body pt-0 position-relative">
          {loadingDetalle && (
            <div className="position-absolute top-0 bottom-0 start-0 end-0 d-flex align-items-center justify-content-center bg-white bg-opacity-75">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label management-label">Cedula</label>
              <div className="input-group">
                <input
                  className="form-control"
                  value={form.documento}
                  onChange={(e) => setForm((prev) => ({ ...prev, documento: e.target.value }))}
                  placeholder="Cedula o documento"
                  disabled={loadingDetalle}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => buscarDocumento?.(isEdit ? "editar" : "nuevo")}
                  disabled={loadingDetalle || buscandoDoc}
                  title="Buscar y autocompletar"
                >
                  {buscandoDoc ? <span className="spinner-border spinner-border-sm" role="status" /> : <i className="bi bi-search" />}
                </button>
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label management-label">Nombres *</label>
              <input
                className="form-control"
                value={form.nombres}
                onChange={(e) => setForm((prev) => ({ ...prev, nombres: e.target.value.toUpperCase() }))}
                required
                disabled={loadingDetalle}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label management-label">Apellidos</label>
              <input
                className="form-control"
                value={form.apellidos}
                onChange={(e) => setForm((prev) => ({ ...prev, apellidos: e.target.value.toUpperCase() }))}
                disabled={loadingDetalle}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label management-label">Telefono *</label>
              <input
                className="form-control"
                value={form.telefono}
                onChange={(e) => setForm((prev) => ({ ...prev, telefono: e.target.value }))}
                placeholder="09xxxxxxx"
                required
                disabled={loadingDetalle}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label management-label">Email</label>
              <input
                type="email"
                className="form-control"
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="correo@dominio.com"
                disabled={loadingDetalle}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label management-label">Fuente de origen</label>
              <select
                className="form-select"
                value={form.fuente_origen_id ?? ""}
                onChange={(e) => {
                  const nextFuenteId = e.target.value;
                  const nextFuenteNombre =
                    fuentesOptions.find((f) => String(f.value) === String(nextFuenteId))?.label || "";
                  const nextFuenteClave = (nextFuenteNombre || "")
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .trim()
                    .toLowerCase();
                  const nextHabilitaReferido = ["administrativo", "estudiante", "docente"].includes(nextFuenteClave);

                  setForm((prev) => ({
                    ...prev,
                    fuente_origen_id: nextFuenteId,
                    fuente_origen_nombre: nextFuenteNombre,
                    referido_por_id: nextHabilitaReferido ? prev.referido_por_id : "",
                  }));

                  if (!nextHabilitaReferido) {
                    setReferidoSearchTerm("");
                    setReferidoMenuAbierto(false);
                  }
                }}
                disabled={loadingDetalle}
              >
                <option value="">Sin fuente</option>
                {fuentesOptions.map((fuente) => (
                  <option key={fuente.value} value={fuente.value}>
                    {fuente.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label management-label">Estado</label>
              <select
                className="form-select"
                value={form.estado_interes}
                onChange={(e) => setForm((prev) => ({ ...prev, estado_interes: e.target.value }))}
                disabled={loadingDetalle}
              >
                {ESTADOS_INTERES.map((estado) => (
                  <option key={estado.value} value={estado.value}>
                    {estado.label}
                  </option>
                ))}
              </select>
            </div>
            {showReferido && (
              <div className="col-md-4">
                <label className="form-label management-label">Referido</label>
                <div className="position-relative">
                  <div className="input-group">
                    <input
                      className="form-control"
                      placeholder="Escribe para buscar (min. 2)"
                      value={referidoInputValue}
                      onFocus={() => {
                        if ((referidoSearchTerm || "").trim()) setReferidoMenuAbierto(true);
                      }}
                      onBlur={() => {
                        setTimeout(() => setReferidoMenuAbierto(false), 150);
                      }}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setReferidoSearchTerm(nextValue);
                        setReferidoMenuAbierto(true);
                        if (
                          form.referido_por_id &&
                          nextValue.trim() !== referidoSeleccionadoLabel.trim()
                        ) {
                          setForm((prev) => ({ ...prev, referido_por_id: "" }));
                        }
                      }}
                      disabled={loadingDetalle}
                    />
                    {!!referidoInputValue && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setReferidoSearchTerm("");
                          setReferidoMenuAbierto(false);
                          setForm((prev) => ({ ...prev, referido_por_id: "" }));
                        }}
                        disabled={loadingDetalle}
                        title="Limpiar"
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    )}
                    {cargandoReferidos && (
                      <span className="input-group-text">
                        <span className="spinner-border spinner-border-sm" role="status" />
                      </span>
                    )}
                  </div>

                  {referidoMenuAbierto && (
                    <div className="list-group int-referido-autocomplete shadow-sm">
                      {opcionesReferidosFiltradas.length > 0 ? (
                        opcionesReferidosFiltradas.map((ref) => {
                          const label = buildReferidoLabel(ref, form.referido_por_id);
                          return (
                            <button
                              key={ref.id}
                              type="button"
                              className={`list-group-item list-group-item-action int-referido-option ${
                                String(ref.id) === String(form.referido_por_id) ? "active" : ""
                              }`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setForm((prev) => ({ ...prev, referido_por_id: String(ref.id) }));
                                setReferidoSearchTerm("");
                                setReferidoMenuAbierto(false);
                              }}
                            >
                              {label}
                            </button>
                          );
                        })
                      ) : (
                        <div className="list-group-item text-muted small">
                          {referidoInputValue.trim().length < 2
                            ? "Ingresa al menos 2 caracteres."
                            : "Sin coincidencias para el termino ingresado."}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {fuenteHabilitaEmpresa && (
              <>
                <div className="col-md-6">
                  <label className="form-label management-label">Empresa de referencia</label>
                  <div className="position-relative">
                    <div className="input-group">
                      <input
                        className="form-control"
                        placeholder="Escribe para buscar (min. 3)"
                        value={empresaInputValue}
                        onFocus={() => {
                          if ((empresaSearchTerm || "").trim().length >= 3) setEmpresaMenuAbierto(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setEmpresaMenuAbierto(false), 150);
                        }}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setEmpresaSearchTerm(nextValue);
                          setEmpresaMenuAbierto(nextValue.trim().length >= 3);
                          setForm((prev) => ({
                            ...prev,
                            empresa_origen_id: "",
                            empresa_nombre: nextValue,
                            nueva_empresa_nombre: "",
                          }));
                        }}
                        disabled={loadingDetalle}
                      />
                      {!!empresaInputValue && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setEmpresaSearchTerm("");
                            setEmpresaMenuAbierto(false);
                            setForm((prev) => ({
                              ...prev,
                              empresa_origen_id: "",
                              empresa_nombre: "",
                              nueva_empresa_nombre: "",
                            }));
                          }}
                          disabled={loadingDetalle}
                          title="Limpiar"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      )}
                    </div>

                    {empresaMenuAbierto && (
                      <div className="list-group int-referido-autocomplete shadow-sm">
                        {empresaSearchTerm.trim().length < 3 ? (
                          <div className="list-group-item text-muted small">Ingresa al menos 3 caracteres.</div>
                        ) : empresaOpcionesFiltradas.length > 0 ? (
                          empresaOpcionesFiltradas.map((empresa) => (
                            <button
                              key={empresa.value}
                              type="button"
                              className="list-group-item list-group-item-action int-referido-option"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  empresa_origen_id: empresa.value,
                                  empresa_nombre: empresa.label,
                                  nueva_empresa_nombre: "",
                                }));
                                setEmpresaSearchTerm("");
                                setEmpresaMenuAbierto(false);
                              }}
                            >
                              {empresa.label}
                            </button>
                          ))
                        ) : (
                          <div className="list-group-item text-muted small">
                            No hay coincidencias. Se guardara como nueva empresa.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {puedeEditarFecha && !isEdit && (
            <div className="mt-3 p-2 border rounded bg-warning-subtle">
              <label className="form-label management-label mb-1">Fecha de registro (migración)</label>
              <input
                type="datetime-local"
                className="form-control form-control-sm"
                value={form.fecha_registro || ""}
                onChange={(e) => setForm((prev) => ({ ...prev, fecha_registro: e.target.value }))}
              />
            </div>
          )}

        </div>
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal} disabled={saving || deleting}>
            Cancelar
          </button>
          <button type="submit" className="btn management-btn-primary" disabled={saving || loadingDetalle}>
            {saving ? "Guardando..." : isEdit ? "Actualizar" : "Crear"}
          </button>
        </div>
      </form>
    );
  };

  return (
    <>
      <Modal open={modal === "crear"} title="Nuevo interesado" onClose={cerrarModal}>
        {renderForm(formNuevo, setFormNuevo, handleCrear)}
      </Modal>

      <Modal open={modal === "editar"} title="Editar interesado" onClose={cerrarModal}>
        {renderForm(formEditar, setFormEditar, handleActualizar, true)}
      </Modal>

      <Modal open={modal === "eliminar"} title="Eliminar interesado" onClose={cerrarModal} size="modal-md">
        <div className="modal-body pt-0">
          Seguro que deseas eliminar a <strong>{interesadoEditando?.nombreCompleto || "este interesado"}</strong>?
          <p className="text-muted mb-0">Esta accion deshabilitara el registro.</p>
        </div>
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal} disabled={deleting}>
            Cancelar
          </button>
          <button type="button" className="btn btn-danger" onClick={handleEliminar} disabled={deleting}>
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </Modal>
    </>
  );
}
